import {Fab, Paper, Zoom, useMediaQuery} from '@material-ui/core';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import {MoreVert} from '@material-ui/icons';
import 'Core/components/TermsAndConditions/LegalDocument.sass';
import MobileLegalDocumentModal from 'Core/components/TermsAndConditions/MobileLegalDocumentModal';
import {clear, getDocumentSummaries, getDocumentText} from 'Core/ducks/legalDocument';
import {isLoaded, toEither} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import HtmlParser from 'react-html-parser';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {renderAsync} from 'Shared/components/RenderAsync';
import {useHistory} from 'react-router';
import {LegalDocumentationRoutesConstants} from './Routes';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';
import {basename} from 'path';

export const LegalDocument = () => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const history = useHistory();
  const {documents, documentText} = useSelector((state: ReducerState) => state.legalDocument);
  const [selectedLegalDocId, setSelectedLegalDocId] = useState<string | undefined>(undefined);
  const [showMobileModal, setShowMobileModal] = useState(false);

  const getKebabFriendlyDocumentPath = (documentName: string | undefined) =>
    toKebabCase(documentName!.replace(/[()]/g, ''));

  useEffect(() => {
    dispatch(getDocumentSummaries());

    return () => {
      dispatch(clear());
    };
  }, [dispatch]);

  const {location} = history;
  const {pathname} = location;

  useEffect(() => {
    if (isLoaded(documents)) {
      const document = documents.Data.find(document => {
        const base = basename(pathname);

        return base === getKebabFriendlyDocumentPath(document.legalDocumentType!.name!);
      });

      if (document) {
        setSelectedLegalDocId(document.id!);
      } else {
        setSelectedLegalDocId(documents.Data[0].id!);
      }
    }
  }, [dispatch, documents, pathname]);

  useEffect(() => {
    if (selectedLegalDocId) {
      dispatch(getDocumentText(selectedLegalDocId));
    }
  }, [dispatch, selectedLegalDocId]);

  return pipe(
    toEither(documents),
    E.fold(renderAsync, docs => (
      <Paper className="legal-document">
        <div className="legal-document__content">
          <div className="content__agreement-list">
            <List component="nav" aria-label="List of Agreements">
              {docs.map(agreement => (
                <ListItem
                  key={`legal-document_${agreement.id!}`}
                  id={`legal-document_${agreement.id!}`}
                  button
                  onClick={() => {
                    history.push(
                      LegalDocumentationRoutesConstants.LEGAL_DOCUMENT(
                        getKebabFriendlyDocumentPath(agreement!.legalDocumentType!.name!)
                      )
                    );
                    dispatch(getDocumentText(agreement.id!));
                  }}
                >
                  {agreement.legalDocumentType!.name}
                </ListItem>
              ))}
            </List>
          </div>
          <div className="content__agreement">
            {pipe(
              toEither(documentText),
              E.fold(renderAsync, docText => (
                <>
                  <div className="content__legal-text">{HtmlParser(docText.legalText!)}</div>
                  {isMobile && (
                    <Zoom in>
                      <Fab
                        color="primary"
                        className="content__change-legal-documents-mobile"
                        onClick={() => setShowMobileModal(true)}
                      >
                        <MoreVert />
                      </Fab>
                    </Zoom>
                  )}
                  <MobileLegalDocumentModal
                    documents={docs}
                    open={showMobileModal}
                    selectedLegalDocumentId={selectedLegalDocId}
                    onChange={setSelectedLegalDocId}
                    onClose={() => {
                      setShowMobileModal(false);
                    }}
                  />
                </>
              ))
            )}
          </div>
        </div>
      </Paper>
    ))
  );
};
export default LegalDocument;
